import { ReactNode, Dispatch, SetStateAction } from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  CalendarMonth as CalendarMonthIcon,
  Update as UpdateIcon,
} from '@mui/icons-material';
import DateRangeFilter from './reporting/DateRangeFilter';
import { FilterDateRange, formatPeriodDisplay, sameDateRange } from '../hooks/useDateRange';
import { useAppSocket } from '../providers/AppSocketContext';
import { useTranslation } from 'react-i18next';
import HubBackButton from '../features/hubs/HubBackButton';
import { DataScopeResource } from '../hooks/useDataScope';

export interface ReportPageHeaderProps {
  title: string;
  icon?: ReactNode;
  period?: FilterDateRange | null;
  showPeriod?: boolean;
  lastUpdated?: Date | null;
  useDateRangeFilter?: boolean;
  setDateRangeFilter?: Dispatch<SetStateAction<FilterDateRange>>;
  defaultDateRangeOption?: string;
  /** Session key for the date range choice; pass the page's useData/useDateRange key. */
  dateRangeKey?: string;
  /** Record types listed; bounds the date filter by the user's read scope. */
  dataScope?: DataScopeResource | DataScopeResource[];
  filters?: ReactNode;
  actions?: ReactNode;
  subtitle?: ReactNode;
  /** Tighter padding when nested inside a table toolbar */
  compact?: boolean;
  showHubBack?: boolean;
}

const ReportPageHeader = ({
  title,
  icon,
  period,
  showPeriod = true,
  lastUpdated,
  useDateRangeFilter = false,
  setDateRangeFilter,
  defaultDateRangeOption,
  dateRangeKey,
  dataScope,
  filters,
  actions,
  subtitle,
  compact = false,
  showHubBack = false,
}: ReportPageHeaderProps) => {
  const { appSettings } = useAppSocket();
  const { t, i18n } = useTranslation();
  const themeColor = appSettings?.UI_THEME_PRIMARY?.value || '#8b1010';

  const periodLabel = period
    ? (period.from === null && period.to === null
        ? t('common:dateRange.allTime')
        : formatPeriodDisplay(period, i18n.language))
    : null;

  const lastUpdatedLabel = lastUpdated
    ? new Date(lastUpdated).toLocaleString(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: compact ? 1.5 : 2,
        mx: compact ? 0 : 1,
        p: compact ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 2.5 },
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
        borderTop: `3px solid ${themeColor}`,
        background: (theme) =>
          `linear-gradient(180deg, ${alpha(themeColor, 0.05)} 0%, ${theme.palette.background.paper} 72%)`,
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', lg: 'flex-start' }}
        spacing={2}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {showHubBack ? <HubBackButton /> : null}
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
            {icon && (
              <Box
                sx={{
                  color: themeColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: alpha(themeColor, 0.1),
                  flexShrink: 0,
                }}
              >
                {icon}
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                component="h1"
                fontWeight={700}
                sx={{ color: themeColor, lineHeight: 1.2 }}
                noWrap
              >
                {title}
              </Typography>
              {subtitle}
            </Box>
          </Stack>

          {(showPeriod && periodLabel) || lastUpdatedLabel ? (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.25 }}>
              {showPeriod && periodLabel && (
                <Chip
                  icon={<CalendarMonthIcon sx={{ fontSize: '16px !important' }} />}
                  label={`${t('common:tableView.period')}: ${periodLabel}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontWeight: 500,
                    borderColor: alpha(themeColor, 0.35),
                    bgcolor: alpha(themeColor, 0.04),
                  }}
                />
              )}
              {lastUpdatedLabel && (
                <Chip
                  icon={<UpdateIcon sx={{ fontSize: '16px !important' }} />}
                  label={`${t('common:tableView.lastUpdated')}: ${lastUpdatedLabel}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              )}
            </Stack>
          ) : null}
        </Box>

        {(filters || useDateRangeFilter || actions) && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            spacing={1.5}
            sx={{
              flexShrink: 0,
              minWidth: { sm: useDateRangeFilter ? 220 : undefined },
            }}
          >
            {filters}
            {useDateRangeFilter && setDateRangeFilter && (
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  minWidth: { xs: '100%', sm: 220 },
                }}
              >
                <DateRangeFilter
                  onChange={(from, to) => {
                    setDateRangeFilter((prev) => (sameDateRange(prev, { from, to }) ? prev : { ...prev, from, to }));
                  }}
                  {...(defaultDateRangeOption ? { defaultOption: defaultDateRangeOption } : {})}
                  dataScope={dataScope}
                  storageKey={dateRangeKey}
                  value={period}
                />
              </Box>
            )}
            {actions && (
              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                {actions}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default ReportPageHeader;